import { evaluate } from "./evaluator";

// ExecutionResult: { output: string[], warnLines: string[], newState, objectivesUpdated: bool }

function interpolate(template, parsed, def, state, scenario) {
  return template.replace(/\{\{([\w.-]+)\}\}/g, (_, path) => {
    const parts = path.split(".");

    if (parts[0] === "args" && parts[1]) {
      const argIndex = def.args?.findIndex((a) => a.name === parts[1]) ?? -1;
      return argIndex >= 0 ? (parsed.args[argIndex] ?? "") : (parsed.args[0] ?? "");
    }

    if (parts[0] === "flags" && parts[1]) {
      const val = parsed.flags[parts[1]];
      return typeof val === "string" ? val : val === true ? "true" : "";
    }

    if (path === "elapsed") return String(state.elapsed);

    if (path === "assetDetail") {
      const assetId = parsed.args[0] ?? "";
      const detail = scenario.assetDetails?.[assetId];
      if (detail) return detail;
      return def.output?.notFound
        ? interpolate(def.output.notFound, parsed, def, state, scenario)
        : `No asset '${assetId}' found in this scenario.`;
    }

    return "";
  });
}

function applyTransitions(def, parsed, state, scenario) {
  let next = { ...state, assets: { ...state.assets } };

  for (const t of def.stateTransitions ?? []) {
    if (t.condition && !evaluate(t.condition, next, parsed.args)) continue;

    const target = interpolate(t.target, parsed, def, state, scenario);

    if (target.startsWith("asset:")) {
      const assetId = target.slice(6);
      next.assets = { ...next.assets, [assetId]: t.value };
    } else if (target === "meta:redHerring") {
      const delta = t.value === "+1" ? 1 : (parseInt(t.value, 10) || 0);
      next = { ...next, redHerringCount: next.redHerringCount + delta };
    }
  }

  return next;
}

export function executeCommand(parsed, scenario, state) {
  if (parsed.name === "help") {
    const lines = ["Available commands:", ""];
    for (const cmd of scenario.commandSet) {
      lines.push(`  ${cmd.synopsis}`);
      lines.push(`    ${cmd.description}`);
      lines.push("");
    }
    return { output: lines, warnLines: [], newState: state, objectivesUpdated: false };
  }

  const def = scenario.commandSet.find((c) => c.name === parsed.name);

  if (!def) {
    return {
      output: [`${parsed.name}: command not found.`],
      warnLines: [],
      newState: state,
      objectivesUpdated: false,
    };
  }

  if (def.availableWhen && !evaluate(def.availableWhen, state, parsed.args)) {
    const msg = def.output?.conditionNotMet ?? "That command isn't available yet.";
    return {
      output: [interpolate(msg, parsed, def, state, scenario)],
      warnLines: [],
      newState: state,
      objectivesUpdated: false,
    };
  }

  for (const argDef of def.args ?? []) {
    if (argDef.required) {
      const idx = def.args.indexOf(argDef);
      if (!parsed.args[idx]) {
        return {
          output: [`Usage: ${def.synopsis}`, `  ${argDef.name}: ${argDef.description}`],
          warnLines: [],
          newState: state,
          objectivesUpdated: false,
        };
      }
    }
  }

  // Early exit for wrong target — prevents state changes and log events for bad args
  if (def.completesObjective && typeof def.completesObjective === "object") {
    const co = def.completesObjective;
    const argIdx = def.args?.findIndex((a) => a.name === co.requireArg) ?? 0;
    const actual = parsed.args[argIdx] ?? "";
    if (actual !== co.equals) {
      const tpl = def.output?.wrongTarget
        ?? `Wrong target: '${actual}' is not the device this action should affect.`;
      return {
        output: interpolate(tpl, parsed, def, state, scenario).split("\n"),
        warnLines: [],
        newState: state,
        objectivesUpdated: false,
      };
    }
  }

  let newState = applyTransitions(def, parsed, state, scenario);

  if (def.triggersLogEvents && def.triggersLogEvents.length > 0) {
    newState = {
      ...newState,
      revealedLogIds: [...newState.revealedLogIds, ...def.triggersLogEvents],
    };
  }

  let objectivesUpdated = false;
  const warnLines = [];

  if (def.completesObjective) {
    const co = def.completesObjective;
    let objId = null;

    if (typeof co === "string") {
      objId = co;
    } else {
      const argIdx = def.args?.findIndex((a) => a.name === co.requireArg) ?? 0;
      const actual = parsed.args[argIdx] ?? "";
      if (actual === co.equals) {
        objId = co.objectiveId;
      } else {
        const msg = co.wrongTargetMessage
          ?? `Wrong target: '${actual}' is not the device this action should affect.`;
        warnLines.push(msg);
      }
    }

    if (objId && newState.objectives[objId] && newState.objectives[objId] !== "done") {
      newState = { ...newState, objectives: { ...newState.objectives, [objId]: "done" } };
      objectivesUpdated = true;
    }
  }

  const allDone = Object.keys(newState.objectives).length > 0 &&
    Object.values(newState.objectives).every((s) => s === "done");
  if (allDone && !newState.complete) {
    newState = { ...newState, complete: true };
  }

  const template = def.output?.success ?? `[${parsed.name}] executed.`;
  const rendered = interpolate(template, parsed, def, state, scenario);
  const output = rendered.split("\n");

  return { output, warnLines, newState, objectivesUpdated };
}
